// pages/alarm/AlarmCenter.js —— 报警中心 · 故障报警（doc01 2.10）
// 3 个子模块：
//  报警列表：今日指标 6 张卡片 + 今日报警统计 + 今日设备/等级统计 + 站点 tab + 报警 tab
//  报警统计：按 4 个模块分区（状态 / 告警等级 / 设备类型 / 历史故障时长），每分区含「月份视图 + 年份视图」两张图
//  通知管理：通知规则列表 + 新增 / 编辑通知配置（弹窗）
import React, { useMemo, useState } from 'react';
import {
  Table, Tag, Button, Select, Radio, Checkbox, Space, message, DatePicker,
  Modal, Row, Col,
} from 'antd';
import ScrollTabs from '../../components/ScrollTabs';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  AreaChart, Area, PieChart, Pie, Cell,
} from 'recharts';
import {
  AlertOutlined, BellOutlined, CheckCircleOutlined, AppstoreOutlined, EnvironmentOutlined,
  ClockCircleOutlined, PlusOutlined,
} from '@ant-design/icons';
import PageHead from '../../components/PageHead';
import {
  getFaultTodayMetrics, getFaultHourly, getFaultTodayByDevice, getFaultTodayByLevel,
  getAlarmStationRows, getFaultAlarmList, getAlarmStatSections,
  ALARM_DEVICE_TYPES, ALARM_STAT_MONTH, ALARM_STAT_YEAR,
  NOTIFY_ROWS, NOTIFY_LEVEL_OPTIONS, NOTIFY_FREQ_OPTIONS, NOTIFY_WAY_OPTIONS,
  NOTIFY_LIMIT_OPTIONS, NOTIFY_RECEIVERS,
  STATION_REGIONS,
} from '../../config/stationData';

const TICK = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const TIP = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const PIE_COLORS = ['#00d4ff', '#52e8b0', '#f4c542', '#8b7cf6', '#ff5a7f', '#7a8ba3'];

const METRICS = getFaultTodayMetrics();
const HOURLY = getFaultHourly();
const BY_DEVICE = getFaultTodayByDevice();
const BY_LEVEL = getFaultTodayByLevel();
const STATION_ROWS = getAlarmStationRows();
const ALARM_ROWS = getFaultAlarmList();

// 省 / 市 / 区选项由站点行政区划自动解析（doc 78：不得写死）
// doc01 2.10.1 p43：站点 tab 查询为「省市区三级联动」——市选项随省联动、区选项随省市联动
const REGION_ROWS = Object.values(STATION_REGIONS);
const uniq = (arr) => Array.from(new Set(arr));
const provinceList = () => uniq(REGION_ROWS.map((r) => r.province));
const cityList = (province) => uniq(REGION_ROWS.filter((r) => province === 'all' || r.province === province).map((r) => r.city));
const districtList = (province, city) => uniq(REGION_ROWS
  .filter((r) => (province === 'all' || r.province === province) && (city === 'all' || r.city === city))
  .map((r) => r.district));

const LEVEL_COLOR = { A: 'red', B: 'orange', C: 'blue', 未分级: 'default' };
const STATUS_COLOR = { 发生中: 'red', 已结束: 'green' };

const pad2 = (n) => String(n).padStart(2, '0');
const fmtDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
// 报警 tab 时间筛选：默认近 7 天（含今日），最大筛选范围不超过 15 天（doc01 2.10.1 p43）
const DEFAULT_DAYS = 7;
const MAX_DAYS = 15;

// ---------- 省 / 市 / 区查询区（三级联动，选项由站点行政区划自动解析）----------
const RegionQuery = ({ value, onChange, onQuery, onReset }) => {
  const opts = (allLabel, list) => [{ value: 'all', label: allLabel }, ...list.map((v) => ({ value: v, label: v }))];
  return (
    <Space wrap>
      <span className="filter-label">省</span>
      <Select
        value={value.province}
        onChange={(v) => onChange({ province: v, city: 'all', district: 'all' })}
        style={{ width: 130 }} options={opts('全部省', provinceList())}
      />
      <span className="filter-label">市</span>
      <Select
        value={value.city}
        onChange={(v) => onChange({ ...value, city: v, district: 'all' })}
        style={{ width: 130 }} options={opts('全部市', cityList(value.province))}
      />
      <span className="filter-label">区</span>
      <Select
        value={value.district}
        onChange={(v) => onChange({ ...value, district: v })}
        style={{ width: 140 }} options={opts('全部区', districtList(value.province, value.city))}
      />
      <Button type="primary" onClick={onQuery}>查询</Button>
      <Button onClick={onReset}>重置</Button>
    </Space>
  );
};

// 命中区域筛选（省 / 市 / 区 任一为「全部」即不限制）
const hitRegion = (row, region) => {
  const g = STATION_REGIONS[row.key] || {};
  if (region.province !== 'all' && g.province !== region.province) return false;
  if (region.city !== 'all' && g.city !== region.city) return false;
  if (region.district !== 'all' && g.district !== region.district) return false;
  return true;
};

const StatPie = ({ title, data, donut }) => (
  <div className="panel">
    <div className="panel-title">{title}<small>单位：条</small></div>
    <ResponsiveContainer width="100%" height={240}>
      <PieChart>
        <Tooltip contentStyle={TIP} />
        <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }} iconSize={8} />
        <Pie data={data} dataKey="value" nameKey="name" outerRadius={88} innerRadius={donut ? 52 : 0} paddingAngle={2}>
          {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  </div>
);

// 报警统计分区内的一张图（自身带「月份 / 年份」标签与独立时间选择器 + 图例数值行）
// 缺陷 87：历史故障时长原先把每个数据点都画成一个未标名的彩色圆点——改为一条明确的系列图例（「时长」）
const StatChartCard = ({
  kind, periodLabel, data, selector, onSelectorChange, selectorOptions, donut,
  seriesName = '时长', seriesColor = '#52e8b0',
}) => (
  <div className="stat-chart">
    <div className="stat-chart-head">
      <span className="stat-chart-tag">{periodLabel}</span>
      <Select size="small" value={selector} onChange={onSelectorChange} options={selectorOptions} style={{ minWidth: 104 }} />
    </div>
    {kind === 'pie' || kind === 'donut' ? (
      <div className="alarm-legend">
        {data.map((d, i) => (
          <span key={d.name}><i style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />{d.name} {d.value}</span>
        ))}
      </div>
    ) : (
      <div className="alarm-legend">
        <span><i style={{ background: seriesColor }} />{seriesName}</span>
        <span className="stat-chart-y-unit">故障时长 (h)</span>
      </div>
    )}
    {kind === 'pie' || kind === 'donut' ? (
      <ResponsiveContainer width="100%" height={196}>
        <PieChart>
          <Tooltip contentStyle={TIP} />
          <Pie data={data} dataKey="value" nameKey="name" outerRadius={74} innerRadius={donut ? 44 : 0} paddingAngle={2}>
            {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    ) : (
      <ResponsiveContainer width="100%" height={196}>
        <AreaChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
          <XAxis dataKey="label" tick={TICK} axisLine={false} tickLine={false} interval={3} />
          {/* 单位放在图例外侧，避免 YAxis label 与刻度叠字 / 被裁切 */}
          <YAxis tick={TICK} axisLine={false} tickLine={false} width={44} tickMargin={4} />
          <Tooltip contentStyle={TIP} />
          <Area type="monotone" dataKey="hours" name={seriesName} stroke={seriesColor} fill={seriesColor} fillOpacity={0.16} strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    )}
  </div>
);

// 报警统计：一个模块分区（左：月份视图，右：年份视图），两张图各自带独立时间选择器
// （doc01 2.10.2：按 4 个模块分块，各模块含「月份 / 年份」两张图，均为默认当月当年、可手动筛选）
const StatSection = ({ title, desc, kind, monthData, yearData, donut }) => {
  const [month, setMonth] = useState(ALARM_STAT_MONTH);
  const [year, setYear] = useState(ALARM_STAT_YEAR);
  const monthOptions = ['2022-10', '2022-09', '2022-08'].map((v) => ({ value: v, label: v }));
  const yearOptions = ['2022', '2021', '2020'].map((v) => ({ value: v, label: v }));
  return (
    <div className="panel stat-module">
      <div className="panel-title">{title}{desc ? <small>{desc}</small> : null}</div>
      <div className="stat-module-body">
        <StatChartCard kind={kind} periodLabel="月份" data={monthData} selector={month} selectorOptions={monthOptions} onSelectorChange={setMonth} donut={donut} seriesColor="#4dd8ff" />
        <StatChartCard kind={kind} periodLabel="年份" data={yearData} selector={year} selectorOptions={yearOptions} onSelectorChange={setYear} donut={donut} seriesColor="#52e8b0" />
      </div>
    </div>
  );
};

const AlarmCenter = () => {
  // ---------- 报警列表：站点 tab 的地区筛选 ----------
  const [stationRegion, setStationRegion] = useState({ province: 'all', city: 'all', district: 'all' });
  const [stationRegionApplied, setStationRegionApplied] = useState({ province: 'all', city: 'all', district: 'all' });

  // ---------- 报警统计：查询区（省 / 市 / 区，独立于报警列表，doc01 2.10.2） ----------
  const [statRegion, setStatRegion] = useState({ province: 'all', city: 'all', district: 'all' });
  const [statRegionApplied, setStatRegionApplied] = useState({ province: 'all', city: 'all', district: 'all' });
  const STAT = useMemo(() => getAlarmStatSections(statRegionApplied), [statRegionApplied]);

  // ---------- 报警列表：报警 tab 筛选 ----------
  const [level, setLevel] = useState('all');
  const [deviceType, setDeviceType] = useState('all');
  const [status, setStatus] = useState('all');
  const [stationFilter, setStationFilter] = useState('all');
  const [alarmRange, setAlarmRange] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  // 已提交（点击查询）的报警 tab 条件
  const [applied, setApplied] = useState({ level: 'all', deviceType: 'all', status: 'all', station: 'all', range: null });

  const today = new Date();
  const defFrom = fmtDate(new Date(today.getTime() - (DEFAULT_DAYS - 1) * 24 * 3600 * 1000));
  const defTo = fmtDate(today);

  const handleQueryAlarms = () => {
    let range = alarmRange;
    if (range && range[0] && range[1]) {
      if (range[1].diff(range[0], 'day') + 1 > MAX_DAYS) {
        message.warning(`时间筛选范围最大不超过 ${MAX_DAYS} 天`);
        return;
      }
    } else {
      range = null;
    }
    setApplied({ level, deviceType, status, station: stationFilter, range });
    message.success('已按当前条件查询');
  };

  const resetAlarms = () => {
    setLevel('all'); setDeviceType('all'); setStatus('all'); setStationFilter('all'); setAlarmRange(null);
    setApplied({ level: 'all', deviceType: 'all', status: 'all', station: 'all', range: null });
    message.success('筛选条件已重置');
  };

  const appliedFrom = applied.range && applied.range[0] ? applied.range[0].format('YYYY-MM-DD') : defFrom;
  const appliedTo = applied.range && applied.range[0] ? applied.range[1].format('YYYY-MM-DD') : defTo;

  const alarmFiltered = ALARM_ROWS.filter((a) => {
    const day = a.time.slice(0, 10);
    if (day < appliedFrom || day > appliedTo) return false;
    return (applied.station === 'all' || a.station === applied.station)
      && (applied.level === 'all' || a.level === applied.level)
      && (applied.deviceType === 'all' || a.deviceType === applied.deviceType)
      && (applied.status === 'all' || a.status === applied.status);
  });

  const stationRows = useMemo(
    () => STATION_ROWS.filter((r) => hitRegion(r, stationRegionApplied)),
    [stationRegionApplied],
  );

  // 报警 tab 站点下拉（由演示数据契约的站点列表生成）
  const stationOptions = useMemo(() => {
    const names = Array.from(new Set(ALARM_ROWS.map((a) => a.station)));
    return [{ value: 'all', label: '全部站点' }, ...names.map((n) => ({ value: n, label: n }))];
  }, []);

  // ---------- 通知管理：配置 CRUD（本地演示） ----------
  const blankNotify = { level: '', freq: '高频', ways: ['App'], limit: 20, receivers: [] };
  const [notifyRows, setNotifyRows] = useState(NOTIFY_ROWS);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyEditKey, setNotifyEditKey] = useState(null);
  const [notifyForm, setNotifyForm] = useState(blankNotify);

  const usedLevels = notifyRows.filter((r) => r.key !== notifyEditKey).map((r) => r.type);

  const openCreate = () => { setNotifyEditKey(null); setNotifyForm({ ...blankNotify, level: NOTIFY_LEVEL_OPTIONS.find((l) => !usedLevels.includes(l)) || '' }); setNotifyOpen(true); };
  const startEdit = (row) => {
    setNotifyEditKey(row.key);
    setNotifyForm({ level: row.type, freq: row.freq, ways: row.ways.split('、'), limit: row.limit, receivers: row.receivers.split('、') });
    setNotifyOpen(true);
  };
  const removeNotify = (row) => {
    setNotifyRows((rs) => rs.filter((r) => r.key !== row.key));
    message.success('已删除该条通知配置');
  };
  const saveNotify = () => {
    if (!notifyForm.level) { message.warning('请选择报警等级'); return; }
    const now = new Date();
    const p = (n) => String(n).padStart(2, '0');
    const updated = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())} ${p(now.getHours())}:${p(now.getMinutes())}`;
    const record = {
      type: notifyForm.level,
      freq: notifyForm.freq,
      ways: notifyForm.ways.join('、') || 'App',
      limit: notifyForm.limit,
      receivers: notifyForm.receivers.join('、') || '—',
      updated,
    };
    if (notifyEditKey != null) {
      setNotifyRows((rs) => rs.map((r) => (r.key === notifyEditKey ? { ...r, ...record } : r)));
      message.success('通知配置已更新');
    } else {
      setNotifyRows((rs) => [...rs, { key: Date.now(), ...record }]);
      message.success('通知配置已新增');
    }
    setNotifyOpen(false);
  };

  // ---------- 报警列表：6 张今日指标卡 ----------
  const stats = [
    { label: '今日告警', value: METRICS.alarmToday, unit: '条', icon: <AlertOutlined />, up: true },
    { label: '发生中', value: METRICS.pending, unit: '条', icon: <ClockCircleOutlined />, down: true },
    { label: '已结束', value: METRICS.closed, unit: '条', icon: <CheckCircleOutlined />, up: true },
    { label: '电站总数', value: METRICS.stations, unit: '座', icon: <AppstoreOutlined /> },
    { label: '当前告警', value: METRICS.warnStations, unit: '座', icon: <BellOutlined />, down: true },
    { label: '今日告警', value: METRICS.todayStations, unit: '座', icon: <EnvironmentOutlined /> },
  ];

  const listTab = (
    <>
      <div className="kpi-grid kpi-grid-3">
        {stats.map((s) => (
          <div className="kpi-card" key={`${s.label}-${s.unit}`}>
            <div className="kpi-icon">{s.icon}</div>
            <div className="kpi-label">{s.label}（{s.unit}）</div>
            <div className="kpi-value">{s.value}</div>
            <div className={`kpi-foot ${s.up ? 'up' : ''} ${s.down ? 'down' : ''}`}>今日实时统计</div>
          </div>
        ))}
      </div>

      <div className="panel">
        <div className="panel-title">今日报警统计<small>0-24 时 · 多站报警次数（次）</small></div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={HOURLY} margin={{ top: 6, right: 16, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
            <XAxis dataKey="clock" tick={TICK} axisLine={false} tickLine={false} interval={1} />
            <YAxis tick={TICK} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={TIP} />
            <Bar dataKey="count" name="告警" fill="#00d4ff" radius={[6, 6, 0, 0]} barSize={14} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid-2">
        <StatPie title="今日设备报警统计" data={BY_DEVICE} />
        <StatPie title="今日报警等级统计" data={BY_LEVEL} donut />
      </div>

      <div className="panel" style={{ padding: '8px 18px 18px' }}>
        <ScrollTabs
          items={[
            {
              key: 'station', label: '站点',
              children: (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <RegionQuery
                      value={stationRegion}
                      onChange={setStationRegion}
                      onQuery={() => { setStationRegionApplied(stationRegion); message.success('已按条件查询'); }}
                      onReset={() => { setStationRegion({ province: 'all', city: 'all', district: 'all' }); setStationRegionApplied({ province: 'all', city: 'all', district: 'all' }); message.success('筛选条件已重置'); }}
                    />
                  </div>
                  <Table
                    rowKey="key"
                    dataSource={[...stationRows].sort((a, b) => a.name.localeCompare(b.name, 'zh'))}
                    size="middle"
                    scroll={{ x: 760 }}
                    pagination={{ pageSize: 10 }}
                    columns={[
                      { title: '站点名称', dataIndex: 'name', strong: true },
                      { title: 'A 级', dataIndex: 'a', width: 90 },
                      { title: 'B 级', dataIndex: 'b', width: 90 },
                      { title: 'C 级', dataIndex: 'c', width: 90 },
                      { title: '发生中', dataIndex: 'pending', width: 100 },
                      { title: '已结束', dataIndex: 'closed', width: 100 },
                    ]}
                  />
                </>
              ),
            },
            {
              key: 'alarm', label: '报警',
              children: (
                <>
                  <Space wrap style={{ marginBottom: 12 }}>
                    <span className="filter-label">站点</span>
                    <Select value={stationFilter} onChange={setStationFilter} style={{ width: 160 }} options={stationOptions} />
                    <span className="filter-label">设备类型</span>
                    <Select
                      value={deviceType} onChange={setDeviceType} style={{ width: 140 }}
                      options={[{ value: 'all', label: '全部' }, ...ALARM_DEVICE_TYPES.map((v) => ({ value: v, label: v }))]}
                    />
                    <span className="filter-label">报警等级</span>
                    <Select
                      value={level} onChange={setLevel} style={{ width: 140 }}
                      options={[{ value: 'all', label: '全部' }, { value: 'A', label: 'A 级（故障）' }, { value: 'B', label: 'B 级（告警）' }, { value: 'C', label: 'C 级（预警）' }, { value: '未分级', label: '未分级' }]}
                    />
                    <span className="filter-label">状态</span>
                    <Select
                      value={status} onChange={setStatus} style={{ width: 130 }}
                      options={[{ value: 'all', label: '全部' }, { value: '发生中', label: '发生中' }, { value: '已结束', label: '已结束' }]}
                    />
                    <span className="filter-label">时间</span>
                    <DatePicker.RangePicker
                      value={alarmRange}
                      onChange={setAlarmRange}
                      format="YYYY-MM-DD"
                      style={{ width: 250 }}
                      placeholder={['默认近 7 天', '默认近 7 天']}
                    />
                    <Button type="primary" onClick={handleQueryAlarms}>查询</Button>
                    <Button onClick={resetAlarms}>重置</Button>
                  </Space>
                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, marginBottom: 12 }}>
                    时间精确到天：默认近 7 天（含今日），最大筛选范围不超过 15 天。
                  </div>
                  <Table
                    rowKey="key"
                    dataSource={alarmFiltered}
                    size="middle"
                    scroll={{ x: 1340 }}
                    pagination={{ pageSize: 10 }}
                    rowSelection={{
                      selectedRowKeys: selectedKeys,
                      onChange: setSelectedKeys,
                      getCheckboxProps: (r) => ({ disabled: r.status !== '发生中' }),
                    }}
                    columns={[
                      { title: '站点名称', dataIndex: 'station', width: 140 },
                      { title: '设备类型', dataIndex: 'deviceType', width: 100 },
                      { title: '设备名称', dataIndex: 'device', width: 190 },
                      { title: '报警等级', dataIndex: 'levelText', width: 110, render: (v, r) => <Tag color={LEVEL_COLOR[r.level]}>{v}</Tag> },
                      { title: '报警标题', dataIndex: 'title', strong: true },
                      { title: '原始报警标题', dataIndex: 'origin', width: 170 },
                      { title: '报警发生时间', dataIndex: 'time', width: 170 },
                      { title: '报警结束时间', dataIndex: 'end', width: 170 },
                      { title: '状态', dataIndex: 'status', width: 100, render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag> },
                      {
                        title: '操作', key: 'action', width: 90, fixed: 'right',
                        render: (_, r) => (
                          <Button type="link" className="blk-row-btn" size="small" disabled={r.status !== '发生中'}
                            onClick={() => message.success(`已将该告警转为工单（来源：故障转工单）`)}>
                            转工单
                          </Button>
                        ),
                      },
                    ]}
                  />
                </>
              ),
            },
          ]}
        />
      </div>
    </>
  );

  const statTab = (
    <>
      <div style={{ marginBottom: 16 }}>
        <RegionQuery
          value={statRegion}
          onChange={setStatRegion}
          onQuery={() => { setStatRegionApplied(statRegion); message.success('已按条件查询'); }}
          onReset={() => {
            const def = { province: 'all', city: 'all', district: 'all' };
            setStatRegion(def);
            setStatRegionApplied(def);
            message.success('筛选条件已重置');
          }}
        />
      </div>
      <div className="stat-grid">
        <StatSection title="状态" desc="统计维度：发生中 / 已结束" kind="donut" monthData={STAT.state.month} yearData={STAT.state.year} donut />
        <StatSection title="告警等级" desc="统计维度：A / B / C / 未分级" kind="donut" monthData={STAT.level.month} yearData={STAT.level.year} donut />
        <StatSection title="设备类型" desc="统计维度：站控设备 / PCS / BMS / 双向电表 / 空调 / 摄像头" kind="pie" monthData={STAT.device.month} yearData={STAT.device.year} />
        <StatSection title="历史故障时长" desc="单位：小时（按小时统计）" kind="area" monthData={STAT.hour.month} yearData={STAT.hour.year} />
      </div>
      <div className="blk-note">
        <div className="blk-note-text">以上数据均不包含当日，截止至昨日 24 时；一直在发生中的告警，当天取 0~24 点发生的时长。</div>
        <div className="blk-note-tag">取值口径</div>
      </div>
    </>
  );

  const notifyTab = (
    <>
      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新增通知配置</Button>
      </div>
      <Table
        rowKey="key"
        dataSource={notifyRows}
        size="middle"
        scroll={{ x: 900 }}
        pagination={false}
        columns={[
          { title: '报警类型', dataIndex: 'type', strong: true },
          { title: '报警频率', dataIndex: 'freq', width: 120 },
          { title: '通知方式', dataIndex: 'ways', width: 140 },
          { title: '每日通知上限', dataIndex: 'limit', width: 130 },
          { title: '通知接收人', dataIndex: 'receivers', width: 200 },
          { title: '上次修改时间', dataIndex: 'updated', width: 170 },
          {
            title: '操作', key: 'action', width: 130,
            render: (_, row) => (
              <Space>
                <span style={{ color: '#00d4ff', cursor: 'pointer' }} onClick={() => startEdit(row)}>编辑</span>
                <span style={{ color: '#ff5a7f', cursor: 'pointer' }} onClick={() => removeNotify(row)}>删除</span>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        className="notify-modal"
        title={notifyEditKey != null ? '编辑通知配置' : '新增通知配置'}
        open={notifyOpen}
        onCancel={() => setNotifyOpen(false)}
        width={760}
        footer={[
          <Button key="save" type="primary" onClick={saveNotify}>保存</Button>,
          <Button key="reset" onClick={() => setNotifyForm(blankNotify)}>重置</Button>,
        ]}
      >
        <div className="notify-cfg">
          <Row align="middle" style={{ marginBottom: 20 }}>
            <Col span={5} className="notify-label">报警等级</Col>
            <Col span={19}>
              <Radio.Group value={notifyForm.level} onChange={(e) => setNotifyForm((f) => ({ ...f, level: e.target.value }))}>
                {NOTIFY_LEVEL_OPTIONS.map((l) => (
                  <Radio key={l} value={l} disabled={usedLevels.includes(l)}>{l}</Radio>
                ))}
              </Radio.Group>
            </Col>
          </Row>

          <Row style={{ marginBottom: 20 }}>
            <Col span={5} className="notify-label">报警推送频率</Col>
            <Col span={19}>
              <div className="notify-freq">
                {NOTIFY_FREQ_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={`notify-freq-item${notifyForm.freq === o.value ? ' active' : ''}`}
                    onClick={() => setNotifyForm((f) => ({ ...f, freq: o.value }))}
                  >
                    <b>{o.value}</b>
                    <span>{o.desc}</span>
                  </button>
                ))}
              </div>
            </Col>
          </Row>

          <Row align="middle" style={{ marginBottom: 20 }}>
            <Col span={5} className="notify-label">通知方式</Col>
            <Col span={19}>
              <Checkbox.Group
                value={notifyForm.ways}
                onChange={(v) => setNotifyForm((f) => ({ ...f, ways: v }))}
                options={NOTIFY_WAY_OPTIONS}
              />
            </Col>
          </Row>

          <Row align="middle" style={{ marginBottom: 20 }}>
            <Col span={5} className="notify-label">每日通知上限</Col>
            <Col span={19}>
              <Radio.Group value={notifyForm.limit} onChange={(e) => setNotifyForm((f) => ({ ...f, limit: e.target.value }))}>
                {NOTIFY_LIMIT_OPTIONS.map((n) => <Radio key={n} value={n}>{n}</Radio>)}
              </Radio.Group>
            </Col>
          </Row>

          <Row align="middle" style={{ marginBottom: 8 }}>
            <Col span={5} className="notify-label">通知接收人</Col>
            <Col span={19}>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="请选择通知接收人"
                value={notifyForm.receivers}
                onChange={(v) => setNotifyForm((f) => ({ ...f, receivers: v }))}
                options={NOTIFY_RECEIVERS.map((v) => ({ value: v, label: v }))}
              />
            </Col>
          </Row>
        </div>
      </Modal>
    </>
  );

  return (
    <div>
      <PageHead title="故障报警" desc="报警列表、报警统计与通知管理" />
      <div className="panel" style={{ padding: '8px 18px 18px' }}>
        <ScrollTabs
          defaultActiveKey="list"
          items={[
            { key: 'list', label: '报警列表', children: listTab },
            { key: 'stat', label: '报警统计', children: statTab },
            { key: 'notify', label: '通知管理', children: notifyTab },
          ]}
        />
      </div>
    </div>
  );
};

export default AlarmCenter;
